import { useEffect, useState } from "react";
import { setTitle } from "../../util/useDocumentTitle";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchSurvey, selectSurvey } from "../../store/surveySlice";
import { api } from "../../store/api";
import SurveyReport from "../../models/surveyReport";
import SurveyReportSchool from "../../models/surveyReportSchool";
import { format } from "../../helpers/format";
import { SurveyReportPublishRequest } from "../../models/surveyReportPublishRequest";
import { Panel, PanelBody, PanelFooter } from "../../components/panel/panel";


interface DetailsRouteParams {
  surveyId?: string;
  surveyReportId?: string;
}

const getSchools = (schools?: SurveyReportSchool[]): SurveyReportSchool[] => {
  return [...(schools?.flatMap((school) => getSchools(school.schools)) ?? []), ...(schools?.map((school) => school) ?? [])];
};


export default function SurveyReportProgressScreen() {
  const dispatch = useAppDispatch();
  const params = useParams<keyof DetailsRouteParams>();
  const surveyState = useAppSelector(selectSurvey);
  const survey = surveyState.survey;
  const isEditing = params.surveyReportId && true;

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [error, setError] = useState<string>();
  const [sendEmail, setSendEmail] = useState(true);
  const [regenerate, setRegenerate] = useState(false);
  const [surveyReport, setSurveyReport] = useState<SurveyReport>();
  const [selectedSurveyReportSchool, setSelectedSurveyReportSchool] = useState<SurveyReportSchool[]>([]);

  useEffect(() => {
    if (params.surveyId) {
      const surveyId = parseInt(params.surveyId);
      if (!surveyState.isLoading) {
        if (survey === undefined || survey.id !== surveyId) {
          console.log('Fetching Survey', surveyId, survey);
          dispatch(fetchSurvey(surveyId));
        }
      }
    }
  }, [dispatch, params.surveyId, survey, surveyState.isLoading]);

  useEffect(() => {
    if (surveyState.isLoading) {
      setTitle(['Loading Survey Details...']);
    }
    else if (surveyState.survey) {
      let titleParts = [surveyState.survey.name, 'Surveys'];
      const surveyReportId = parseInt(params.surveyReportId!);

      setIsLoading(true);
      api.survey(survey!.id!).reports.get(surveyReportId)
        .then((result) => {
          setSurveyReport(result);
          //setSelectedSurveyReportSchool(getSchools(result.schools).filter(s => (s.contactEmail?.length ?? 0) > 3));
          setIsLoading(false);
          setError(undefined);
        })
        .catch((reason) => {
          console.error(reason);
          setError(`Unable to load report #${params.surveyReportId}`);
          setIsLoading(false);
        });

      titleParts = ['Publish Shared Report', ...titleParts];
      setTitle(titleParts);
    }
  }, [params.surveyReportId, survey, surveyState.isLoading, surveyState.survey]);

  //const schoolQuestion = survey?.questions?.find(q => q.id === survey?.schoolQuestionId);

  const onAddSchool = (added: SurveyReportSchool) => {
    const flatAdded = { ...added, schools: undefined };
    console.log('added', flatAdded);
    setSelectedSurveyReportSchool(cur => [...cur, flatAdded ]);
  }
  const onRemoveSchool = (remove: SurveyReportSchool) => {
    setSelectedSurveyReportSchool(cur => cur.filter(item => item.id !== remove.id));
  }

  const onShareProgress = () => {
    if (surveyReport) {
      const request: SurveyReportPublishRequest = {
        schools: selectedSurveyReportSchool,
        sendEmail,
        regenerate,
      };
      setIsSaving(true);
      api.survey(surveyReport?.surveyId).report(surveyReport?.id).shareProgress(request)
        .then((result) => {
          setSurveyReport(result);
          setError(undefined);
          setIsLoading(false);
          setIsPublished(true);
          setIsSaving(false);
        })
        .catch((reason) => {
          console.error(reason);
          setError(`Unable to publish report #${params.surveyReportId}`);
          setIsLoading(false);
        });
    }
  };

  return (<div>
    <div className="d-flex align-items-center mb-3">
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">Surveys</li>
        {surveyState.isLoading && <li className="breadcrumb-item">Loading...</li>}
        {surveyState.survey && <li className="breadcrumb-item"><Link to='../..'>{surveyState.survey.name}</Link></li>}
        {surveyReport && <li className="breadcrumb-item"><Link to={"../edit"} relative="path">{surveyReport.name}</Link></li>}
        <li className="breadcrumb-item active">Share Progress</li>
      </ol>
    </div>
    <div className="row">
      <div className="col-12">
        {surveyState.error && <div className="alert alert-danger"><strong>Error!</strong> {surveyState.error}</div>}
        <Panel isLoading={isLoading || isSaving}>
          <PanelBody>
            <div className="card border-0 mb-4">
              <div className="card-header bg-none h6 p-3 m-0 d-flex align-items-center justify-content-between">
                <div>Report System(s)/School(s)</div>
                <Link className="btn btn-sm btn-outline-primary" to={"../edit"} relative="path">Edit</Link>
              </div>
              <div className="card-body">
                {isLoading && <span>Fetching existing report details...</span>}
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      {isPublished === false && <th></th>}
                      <th>School/System</th>
                      <th>Contact Person</th>
                      <th>Contact Email</th>
                      <th className="text-end"># Responses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveyReport?.schools?.map((school) => <RenderSchool
                      key={school.id}
                      school={school}
                      indent={1}
                      selected={selectedSurveyReportSchool}
                      surveyId={surveyReport.surveyId}
                      onAdd={isPublished ? undefined : onAddSchool}
                      onRemove={isPublished ? undefined : onRemoveSchool}
                    />)}
                  </tbody>
                </table>
              </div>
            </div>
            <p>
              Clicking <i>Share Progress</i> below will send an email to each of the selected contacts with information on the number of responses for their school(s).
            </p>
          </PanelBody>
          
          {isPublished && <PanelFooter>
            <div className="alert alert-success">
              <strong>Published!</strong> The progress for the select school(s) will be sent shortly!
              {sendEmail && <p>The contacts will been sent an email with details on the number of responses for their school(s).</p>}
            </div>
          </PanelFooter>}
        </Panel>
      </div>
    </div>
    <div className="row">
      <div className="col">
        {error && <div className="border-1 alert alert-danger"><strong>Error</strong> {error}</div>}
        {isPublished === false && <button type="button" className="btn btn-success me-2" disabled={isSaving || selectedSurveyReportSchool.length === 0} onClick={() => onShareProgress()}>Share Progress with Contacts</button>}
        <Link to='../..' className="btn btn-primary me-2">Back to {survey?.name}</Link>
      </div>
    </div>

  </div>);

}

type RenderSchoolProps = {
  school: SurveyReportSchool;
  selected: SurveyReportSchool[];
  indent: number;
  surveyId: number;
  onAdd?: (added: SurveyReportSchool) => void;
  onRemove?: (added: SurveyReportSchool) => void;
}
function RenderSchool(props: RenderSchoolProps) {
  const { school, indent, selected, surveyId, onAdd, onRemove } = props;

  const isSelected = selected.find((s) => s.id === school.id) !== undefined;

  const onChangeSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onAdd && onRemove) {
      if (isSelected) {
        onRemove(school);
      }
      else {
        onAdd(school);
      }
    }
  };

  return <>
    <tr>
      {onAdd && onRemove && <td className={`ps-${indent}`}>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            value={school.id}
            id={`school_${school.id}`}
            disabled={(school.contactEmail ?? '').length <= 3}
            checked={isSelected}
            onChange={onChangeSelected}
          />
        </div>
      </td>}
      <td className={`ps-${indent}`}>
        {school.choice === undefined ? <i className="fa-fw fa-solid fa-building text-primary"></i> : <i className="fa-fw fa-solid fa-school"></i>}
        <label className="form-check-label ms-1" htmlFor={`school_${school.id}`}>
          {school.name ?? school.choice?.choiceText}
        </label>
      </td>
      <td>
        {school.contactName}<br />
        {school.contactRole}
      </td>
      <td>
        {school.contactEmail}
      </td>
      <td className="text-end">
        {school.choice && <ResponseCount surveyId={surveyId} choiceId={school.choice.id} />}
      </td>
    </tr>
    {school.schools?.map((subschool) => <RenderSchool school={subschool} indent={indent + 1} selected={selected} surveyId={surveyId} onAdd={onAdd} onRemove={onRemove} />)}
  </>;
}

type ShareLinkProps = {
  shareToken: string;
}

function ShareLink(props: ShareLinkProps) {
  const { shareToken } = props;
  const shareUrl = `${window.location.protocol}//${window.location.host}/share/${encodeURIComponent(shareToken)}/surveyReport`;

  const [isCopied, setIsCopied] = useState(false);

  const onCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 1200);
    }
  };

  return <div className="input-group input-group-sm">
    <button className={"btn btn-sm " + (isCopied ? 'btn-success' : 'btn-outline-secondary')} type="button" title="Copy to clipboard" onClick={() => onCopy()}><i className="fa-solid fa-paste"></i></button>
    <input type="text" value={shareUrl} className={"form-control " + (isCopied ? 'border-success' : '')} />
  </div>;
}

type ResponseCountProps = {
  surveyId: number;
  choiceId: number;
}
function ResponseCount(props: ResponseCountProps) {
  const [count, setCount] = useState<number>();

  useEffect(() => {
    api.survey(props.surveyId).responses.count({ choiceId: props.choiceId }).then(setCount);
  }, [props.choiceId, props.surveyId]);

  if (count) {
    return <span title={`${format.number(count)} responses for this school`}>{format.number(count)}</span>;
  }
  return <></>;
}